import { useDispatch, useSelector } from "react-redux";
import SearchBox from "components/__new/common/SearchBox";
import Table from "components/__new/common/Table";
import Filter from "components/__new/common/Filter";
import { ReactComponent as IconAdd } from "assets/images/icons/project-icons/Plus.svg"
import { ReactComponent as IconPause } from "assets/images/icons/project-icons/pause.svg"
import { ReactComponent as IconBank } from "assets/images/icons/project-icons/default-bank-logo.svg"
import { TableContent } from "components/__new/common/TableContent";
import CurrencyFormat from "utils/currencyFormat";
import Pagination from "components/__new/common/pagination";
import Maybe from "components/__new/common/Maybe";
import ContentLoading from "components/__new/common/ContentLoading";
import pluralize from "utils/pluralize";
import TableEmptyData from "components/__new/common/TableEmptyData";
import DropdownNew from "utils/dropdown";
import ViewRecurringTransferDetails from "./ViewRecurringTransferDetails";
import { useState } from "react";
import { Link } from "react-router-dom";
import PauseRecurringTransfer from "./PauseRecurringTransfer";
import DeleteRecurringTransfer from "./DeleteRecurringTransfer";
import moment from "moment";
import { clearRecurringTransferForm, deleteRecurringTransfer, pauseRecurringTransfer, selectRecurringTransfer } from "store/entities/transfers/action";
import { renderStatusColor } from "utils/statusColor";


export const RecurringTransfersTable = ({toggleRecurringTransferSetup, getRecurringTransfers, toggleEditModal}) => {
    const dispatch = useDispatch();
    
    const [selected, setSelected] = useState(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    
    const [pauseTransferOpen, setPauseTransferOpen] = useState(false);
    const [deleteTransferOpen, setDeleteTransferOpen] = useState(false);
    
    const isPaused = selected?.status === 'paused';
    
    const {
        allRecurringTransfersLoading: loading,
        allRecurringTransfersData: data,
        pauseRecurringLoading,
        deleteRecurringLoading
    } = useSelector((s) => s.entities.transfers);
    
    
    const refresh = () => {
        setSelected(null);
        setPauseTransferOpen(false);
        setDeleteTransferOpen(false)
        getRecurringTransfers();
    }
    
    const pauseResume = () => {
        dispatch(pauseRecurringTransfer({
            id: selected?._id,
            status: isPaused ? "active" : "paused",
        })).then(() => refresh());
    }
    
    const deleteTransfer = () => {
        dispatch(deleteRecurringTransfer(selected?._id)).then(() => refresh());
    }
        
    return (
        <div>
            
            <Maybe condition={isDetailsOpen}>
                <ViewRecurringTransferDetails
                    selected={selected}
                    isOpen={isDetailsOpen}
                    setIsOpen={setIsDetailsOpen}
                />
            </Maybe>
            
            <PauseRecurringTransfer
                loading={pauseRecurringLoading}
                isOpen={pauseTransferOpen}
                setIsOpen={setPauseTransferOpen}
                onProceed={pauseResume}
                isPaused={isPaused}
            />
            
            <DeleteRecurringTransfer
                loading={deleteRecurringLoading}
                isOpen={deleteTransferOpen}
                setIsOpen={setDeleteTransferOpen}
                onProceed={deleteTransfer}
            />
            
            <div className="mt-6 box">
                
                <div className="flex items-center justify-start w-max">
                    <div className="font-medium">Recurring Transfers&nbsp;</div>
                    
                    <div className="label h-6 text-sm font-normal text-ep-primary-600 bg-[#ecf2f2]">
                        <ContentLoading
                            loading={loading}
                            data={<>{data?.data?.totalDocs ?? "-"} {pluralize(data?.data?.totalDocs, "transfer", "transfers")}</>}
                        />
                    </div>
                </div>
                
                <div className="mt-6 flex flex-wrap justify-between">
                    <div className="max-w-sm flex-grow sm:mr-6">
                        <SearchBox placeholder="Search" name="keyword" extraUrlParams="page=1" />
                    </div>
                        
                    <div className="flex flex-wrap space-x-3 sm:space-x-3 mt-5 lg:mt-0">
                        <div>
                            <Filter filters={[
                                {
                                    title: "Date range",
                                    name: "dateRange",
                                    dataType: "custom-timeframe",
                                    hasResetButton: true,
                                    options: [
                                        {
                                            display: "Today",
                                            payload: "today",
                                        },
                                        {
                                            display: "Last 7 days",
                                            payload: "7days",
                                        },
                                        {
                                            display: "30 days",
                                            payload: "30days",
                                        },
                                        {
                                            display: "1 Year",
                                            payload: "1year",
                                        },
                                    ],
                                },
                                {
                                    title: "Frequency",
                                    name: "frequency",
                                    dataType: "select",
                                    options: [
                                        {
                                            display: "All",
                                            payload: "",
                                        },
                                        {
                                            display: "Daily",
                                            payload: "daily",
                                        },
                                        {
                                            display: "Weekly",
                                            payload: "weekly",
                                        },
                                        {
                                            display: "Monthly",
                                            payload: "monthly",
                                        }
                                    ],
                                },
                                {
                                    title: "Status",
                                    name: 'status',
                                    dataType: 'select',
                                    options: [
                                        {
                                            display: 'Ongoing',
                                            payload: 'active'
                                        },
                                        {
                                            display: 'Paused',
                                            payload: 'paused',
                                        },
                                        {
                                            display: 'Cancelled',
                                            payload: 'cancelled'
                                        }
                                    ]
                                }
                            ]}/>
                        </div>

                        <div>
                            <button type="button" onClick={() => {
                                dispatch(clearRecurringTransferForm());
                                toggleRecurringTransferSetup();
                            }} className="btn btn-ep-primary rounded-lg flex items-center justify-center h-full">
                                <IconAdd width={15} className="mr-2" />
                                <span>New Recurring transfer</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white">

                <Table>
                    <Table.Head>
                        <th>Name</th>
                        <th>Amount</th>
                        <th>Frequency</th>
                        <th>Recipient</th>
                        <th>Next Transfer</th>
                        <th></th>
                    </Table.Head>
                    <Table.Body>

                        <TableContent
                            loading={loading}
                            loadingText="Fetching Transfers"
                            data={data?.data}
                            colspan={6}
                            emptyUI={
                                <TableEmptyData
                                    useSubtitle
                                />
                            }
                        />
                        
                        {loading === false && data?.data?.docs?.map((transaction) => {
                            const daysLeft = moment(transaction?.nextTransfer, 'YYYY-MM-DD').diff(new Date(), 'days');
                            const accountName = `${transaction?.recipient?.accountName?.split(" ")[0]} ${transaction?.recipient?.accountName?.split(" ")[1]}`
                            
                            return (
                                <tr key={transaction._id}>
                                    <td className="capitalize">
                                        {transaction?.name}
                                    </td>
                                    <td className="capitalize">
                                        {CurrencyFormat(transaction?.amount)}
                                    </td>
                                    <td>
                                        <div className="capitalize py-2">
                                            {transaction.frequency}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="capitalize flex items-center">
                                            <IconBank width={28} />
                                            
                                            <div className="capitalize ml-2.5">
                                                <span className="font-medium">{accountName?.toLowerCase()}</span>
                                                
                                                <div className="flex items-center">
                                                    {transaction?.recipient?.bankName?.toLowerCase() || "N/A"}
                                                    
                                                    <span className='h-1 w-1 rounded-full bg-gray-300 mx-2' />
                                                    
                                                    {transaction?.recipient?.accountNumber?.toLowerCase() || "N/A"}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <Maybe condition={transaction?.status === 'paused'}>
                                            <div className={"flex items-center justify-center text-sm rounded-lg " + renderStatusColor('pending')}>
                                                <IconPause className="mr-1" width={12} />
                                                Paused
                                            </div>
                                        </Maybe>
                                        
                                        <Maybe condition={transaction?.status !== 'paused'}>
                                            <div className="flex items-center">
                                                <span className="font-semibold text-ep-primary-500">{daysLeft < 1 ? 0 : daysLeft}&nbsp;</span>
                                                {`${(daysLeft > 1 || daysLeft < 1) ? 'Days' : 'Day'}`}
                                            </div>
                                        </Maybe>
                                    </td>
                                    <td>
                                        <DropdownNew flipSwitch>
                                            <></>
                                            <div className="flex flex-col items-start">
                                                <button onClick={() => {
                                                    setSelected(transaction);
                                                    setIsDetailsOpen(true);
                                                }} className="dropdown-item pr-8">
                                                    View Details
                                                </button>
                                                
                                                <Link to={`/payments/transfers/recurring-transfers/${"abc"}/history`} className="dropdown-item pr-8">
                                                    View History
                                                </Link>
                                                
                                                <Maybe condition={transaction?.status === 'paused'}>
                                                    <button onClick={() => {
                                                        setSelected(transaction);
                                                        setPauseTransferOpen(true);
                                                    }} className="dropdown-item pr-8 w-full">
                                                        Resume
                                                    </button>
                                                </Maybe>
                                                
                                                <Maybe condition={transaction?.status !== 'paused'}>
                                                    <button onClick={() => {
                                                        setSelected(transaction);
                                                        setPauseTransferOpen(true);
                                                    }} className="dropdown-item pr-8 w-full">
                                                        Pause
                                                    </button>
                                                </Maybe>
                                                
                                                <button onClick={() => {
                                                    dispatch(selectRecurringTransfer(transaction));
                                                    toggleEditModal();
                                                }} className="dropdown-item pr-8 w-full">
                                                    Edit
                                                </button>
                                                
                                                <button onClick={() => {
                                                    setSelected(transaction);
                                                    setDeleteTransferOpen(true);
                                                }} className="dropdown-item pr-8 w-full text-[#dc2626]">
                                                    Delete
                                                </button>
                                                
                                            </div>
                                        </DropdownNew>
                                    </td>
                                </tr>
                            )}
                        )} 
                    </Table.Body>
                </Table>

                <Maybe condition={loading === false && data?.data?.docs?.length > 0}>
                    <div className="mt-4">
                        <Pagination data={data?.data} />
                    </div>
                </Maybe>

            </div>
        </div>
    )
}


export default RecurringTransfersTable;